import React, { useState, useCallback, useEffect } from "react";
import Autocomplete, { AutocompleteCloseReason } from "@mui/material/Autocomplete";
import { debounce } from "lodash";

// Components
import { ListBoxVehicleComponent, ID_LOADING, ID_RESULTS_FOUND } from "./components/ListBoxVehicleComponent";
import { TextFieldVehicleComponent } from "./components/TextFieldVehicleComponent";

// Models
import { VehicleAutoCompleteResponse } from "@/main/models/response";
import { VehicleAutocompleteFilterRequest } from "@/main/models/request";

// Api's
import { vehicleApi } from "@/main/api";

// Styles
import { useStyles } from "./style";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";

// Utils & Enums
import { getDefaultMessageResultsFound, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";
import { IGridData, VehicleAutoCompleteProps } from "./interfaces";
import { useSearchParams } from "react-router-dom";

export default function VehicleAutoCompleteComponent({
	handleChangeVehicle,
	vehicleAutoCompleteType,
	hasBorderStyle,
	hasClearValue,
	setHasClearValue,
	label,
	disabled,
	vehicleValueProps,
}: VehicleAutoCompleteProps) {
	const [searchParams] = useSearchParams();
	const vehicleSearchParams = searchParams.get("vehicle");

	// Styles
	const classes = useStyles();

	// States
	const INITIAL_GRID_STATE: IGridData = {
		totalItems: 0,
		totalNumberOfPages: 0,
		isLoading: false,
		data: [],
	};
	const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
	const [vehicleSelected, setVehicleSelected] = useState<VehicleAutoCompleteResponse | null>(null);
	const [inputTextApi, setInputTextApi] = useState<string>("");
	const [inputText, setInputText] = useState<string>("");
	const [pageIndex, setPageIndex] = useState<number>(PaginationEnum.PageIndexIntial);
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<boolean>(false);
	const [hasShowMore, setHasShowMore] = useState<boolean>(false);

	// Constants
	const isPartialAutoComplete = vehicleAutoCompleteType === VehicleAutoCompleteTypeEnum.Partial;
	const labelDefault =
		label ||
		`${getTranslateByKey("information.make")} / ${getTranslateByKey(
			"information.model",
		)} / ${getTranslateByKey("information.trim")}`;

	// Search term
	useEffect(() => {
		if (inputTextApi && inputTextApi.length >= 3) {
			let optionsData: VehicleAutoCompleteResponse[] = [];

			(async () => {
				setGridData({
					isLoading: true,
					totalItems: 0,
					totalNumberOfPages: 0,
					data: [],
				});
				const index = PaginationEnum.PageIndexIntial;
				setPageIndex(index);
				const request: VehicleAutocompleteFilterRequest = {
					searchTerm: inputTextApi,
					pageIndex: index,
					pageSize: PaginationEnum.PageSize10,
				};
				const responseApi = await getData(request);
				if (responseApi.data.results && responseApi.data.results.length > 0) {
					optionsData = responseApi.data.results;
				}
				setGridData({
					isLoading: false,
					totalItems: responseApi.data.totalNumberOfRecords,
					totalNumberOfPages: responseApi.data.totalNumberOfPages,
					data: optionsData,
				});
				setHasShowMore(optionsData.length < responseApi.data.totalNumberOfRecords);
				setResultsSpan(optionsData.length, responseApi.data.totalNumberOfRecords);
			})();
		}
	}, [inputTextApi]);

	useEffect(() => {
		const elementLoading = document.getElementById(ID_LOADING);
		if (elementLoading) {
			if (isLoadingInfiniteScroll) {
				elementLoading.innerText = `${getTranslateByKey("information.searchingForVehicles")}...`;
			} else {
				elementLoading.innerText = "";
			}
		}
	}, [isLoadingInfiniteScroll]);

	useEffect(() => {
		if (setHasClearValue && hasClearValue) {
			resetAllState();
			setVehicleSelected(null);
			setHasClearValue(false);
		}
	}, [hasClearValue]);
	useEffect(() => {
		if (vehicleSearchParams) {
			setInputTextApi(vehicleSearchParams);
			setInputText(vehicleSearchParams);
		}
	}, [vehicleSearchParams]);
	useEffect(() => {
		if (!isNullUndefined(vehicleValueProps)) {
			const val = vehicleValueProps ?? "";
			if (val !== "") {
				setInputText(val);
			} else if (!vehicleSearchParams) {
				setInputText("");
				setInputTextApi("");
				setVehicleSelected(null);
			}
		}
	}, [vehicleValueProps, vehicleSearchParams]);
	const debounceOnChange = useCallback(
		debounce((valueTyping: string) => {
			setInputTextApi(valueTyping);
		}, 600),
		[],
	);

	function setResultsSpan(dataLength: number, totalItems: number) {
		const element = document.getElementById(ID_RESULTS_FOUND);
		if (element) {
			element.innerText = getDefaultMessageResultsFound(dataLength, totalItems);
		}
	}

	function getGroupName(option: VehicleAutoCompleteResponse) {
		if (option.guidSubModel === undefined && option.guidVehicleModel === undefined) {
			return getTranslateByKey("information.make");
		}

		return labelDefault;
	}

	function getSearchTerm() {
		if (vehicleSelected !== null && vehicleSelected.value) {
			if (inputText !== "" && vehicleSelected.value !== inputText) {
				return inputText;
			}

			return vehicleSelected.value;
		}

		return inputText;
	}

	// Async' functions
	async function getData(request: VehicleAutocompleteFilterRequest) {
		return isPartialAutoComplete
			? vehicleApi.getVehicleAutocompletePartial(request, false)
			: vehicleApi.getVehicleAutocompleteFullWithYear(request, false);
	}

	// Reset's
	function clearSearchTerms() {
		setInputText("");
		setInputTextApi("");
	}

	function resetAllState() {
		setGridData(INITIAL_GRID_STATE);
		clearSearchTerms();
	}

	function resetStatesScrollInfinity() {
		setPageIndex(PaginationEnum.PageIndexIntial);
		setHasShowMore(false);
	}

	// Infinite scroll
	function onLoadInfiniteScroll(request: VehicleAutocompleteFilterRequest) {
		setIsLoadingInfiniteScroll(true);
		getData(request)
			.then((response) => {
				let resultData: VehicleAutoCompleteResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
				}
				if (response.data && response.data.totalNumberOfRecords && response.data.totalNumberOfRecords > 0) {
					resultTotalItems = response.data.totalNumberOfRecords;
				}
				if (gridData.data.length > 0) {
					resultData = [...gridData.data, ...resultData];
				}
				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					totalNumberOfPages: response.data.totalNumberOfPages,
					data: resultData,
				});
				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
				setResultsSpan(resultData.length, resultTotalItems);
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid() {
		if (gridData.totalNumberOfPages >= pageIndex + 1) {
			const index = pageIndex + PaginationEnum.PageIndexIntial;
			setPageIndex(index);
			const request: VehicleAutocompleteFilterRequest = {
				searchTerm: inputTextApi,
				pageIndex: index,
				pageSize: PaginationEnum.PageSize10,
			};
			onLoadInfiniteScroll(request);
		}
	}

	const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
		const listboxNode = event.currentTarget;

		const position = listboxNode.scrollTop + listboxNode.clientHeight;
		if (listboxNode.scrollHeight - position <= 1 && hasShowMore) {
			onLoadSearchGrid();
		}
	};

	return (
		<div id="id-vehicle-autocomplete-container">
			<Autocomplete
				freeSolo={true}
				className={classes.inputAutoComplete}
				noOptionsText={getTranslateByKey("information.noRecordsFound")}
				id="id-vehicle-autocomplete-field"
				size="small"
				loading={gridData.isLoading}
				loadingText={`${getTranslateByKey("information.searchingForVehicles")}...`}
				value={vehicleSelected}
				inputValue={inputText}
				filterOptions={(filteredOptions: any[]) => filteredOptions}
				groupBy={isPartialAutoComplete ? (option: VehicleAutoCompleteResponse) => getGroupName(option) : () => ""}
				options={gridData.data}
				renderOption={(props, option: VehicleAutoCompleteResponse) => {
					const { key, ...restProps } = props as React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
					return (
						<li key={key} {...restProps}>
							{option.value}
						</li>
					);
				}}
				getOptionLabel={(option: string | VehicleAutoCompleteResponse) => {
					if (typeof option === "string") return option;
					return option ? option.value : "";
				}}
				onChange={(_: React.SyntheticEvent, vehicleChange: string | VehicleAutoCompleteResponse | null) => {
					if (isPartialAutoComplete) {
						setVehicleSelected(vehicleChange as VehicleAutoCompleteResponse | null);
					}

					handleChangeVehicle(vehicleChange as VehicleAutoCompleteResponse | null);
				}}
				renderInput={(params) => {
					return (
						<TextFieldVehicleComponent
							params={{
								...params,
								inputProps: {
									...params.inputProps,
									onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
										params.inputProps?.onBlur?.(e);

										if (e.target.value === "") {
											setVehicleSelected(null);
											handleChangeVehicle(null);
											resetAllState();
											resetStatesScrollInfinity();
										}
									},
								},
							}}
							propsCustom={{
								searchTerm: getSearchTerm(),
								labelDefault,
								setInputText,
								hasBorderStyle,
							}}
						/>
					);
				}}
				onInputChange={(_: React.SyntheticEvent, newInputValue: string, reason: string) => {
					if (reason === "clear") {
						resetAllState();
					} else if (reason === "reset") {
						if (isPartialAutoComplete) {
							setInputText(newInputValue);
							setInputTextApi("");
						} else {
							clearSearchTerms();
						}
					} else if (reason === "input") {
						setInputText(newInputValue);
						debounceOnChange(newInputValue);
					}

					// Clear field (Backspace)
					if (newInputValue === "") {
						resetStatesScrollInfinity();
						resetAllState();
						setVehicleSelected(null);
						handleChangeVehicle(null);
					}
				}}
				onClose={(_: React.SyntheticEvent, reason: AutocompleteCloseReason) => {
					const keepsSelectedVehicleText =
						isPartialAutoComplete && (reason === "selectOption" || vehicleSelected !== null);

					if (!keepsSelectedVehicleText) {
						resetAllState();
					}

					resetStatesScrollInfinity();
				}}
				ListboxProps={{
					onScroll: handleScroll,
				}}
				ListboxComponent={ListBoxVehicleComponent}
				disablePortal={true}
				disabled={disabled}
			/>
		</div>
	);
}
